import React, { useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import { Input, Flex, InputSelect, Box, Text, InputGroup } from '@revolut/ui-kit'
import { countries } from 'helpers/countries'
import allowedCountries from 'helpers/allowedCountries.json'

import { fetchCountryCode } from '../../services/api'

const phoneNumbers = Object.values(countries)
  .filter(({ countryCode }) => allowedCountries.includes(countryCode))
  .map(({ phoneCode, country }) => ({
    label: `+${phoneCode}`,
    value: `+${phoneCode}`,
    countryName: country,
  }))

const FloatingError = styled(Text).attrs({
  color: 'error',
  variant: 'caption',
})`
  position: absolute;
`

type Props = {
  error: {
    code?: string
    number?: string
  }
  value: {
    code?: string
    number?: string
  }
  onChange: (field: string, value?: string) => void
}

type PhoneNumber = {
  label?: string
  value?: string
  countryName?: string
}

const PhoneField = ({ error = {}, value = {}, onChange }: Props) => {
  const [countryFetched, setCountryFetched] = useState<boolean>(false)
  const sortedOptions = useMemo(
    () => [...phoneNumbers].sort((a, b) => a.countryName.localeCompare(b.countryName)),
    [],
  )

  useEffect(() => {
    fetchCountryCode()
      .then((data) => {
        if (data.country) {
          if (countries[data.country] && allowedCountries.includes(data.country)) {
            onChange('phone.code', `+${countries[data.country].phoneCode}`)
          }
        }
      })
      .finally(() => {
        setCountryFetched(true)
      })
  }, [onChange])
  return (
    <InputGroup variant='horizontal'>
      <InputSelect
        disabled={!countryFetched}
        dropdown={{ fitInAnchor: false }}
        hasError={false}
        name='phone.code'
        placeholder='Code'
        value={value.code}
        options={sortedOptions}
        onChange={(selectedValue: string) => {
          onChange('phone.code', selectedValue)
        }}
        renderOption={(item: PhoneNumber, select: { getOptionProps: Function }) => (
          <InputSelect.Option key={item.countryName} {...select.getOptionProps(item)}>
            <Flex>
              <Box>
                <Text>{item.countryName}</Text>
              </Box>
              <Box ml='auto'>
                <Text>{item.label}</Text>
              </Box>
            </Flex>
          </InputSelect.Option>
        )}
      />
      <Input
        name='phone.number'
        value={value.number}
        hasError={!!(error.number || error.code)}
        message={<FloatingError>{error.number || error.code}</FloatingError>}
        parseFn={(_value: string) => _value.replace(/[^\d]/g, '')}
        type='tel'
        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
          onChange('phone.number', e.target.value)
        }
        placeholder='Mobile phone number'
      />
    </InputGroup>
  )
}

export default PhoneField
